/**
 * v-tooltip directive powered by tippy.js
 *
 * Drop-in replacement for floating-vue's v-tooltip directive.
 * Each tooltip is an independent tippy.js instance — no shared reactive
 * state, so no recursive-update cascade with many tooltips on a page.
 *
 * Supports:
 *   v-tooltip="'string'"
 *   v-tooltip="{ content, placement, popperClass }"
 *   v-tooltip.bottom="'string'"
 */
import 'tippy.js/dist/tippy.css'
import tippy from 'tippy.js'

import { sanitizeHtml } from '@/utils/sanitize'

const DISTANCE = 10

const defaults = {
  allowHTML: true,
  delay: [200, 100],
  offset: [0, DISTANCE],
  arrow: true,
  hideOnClick: true,
  interactive: true,
  appendTo: () => document.body,
  // Bound to viewport so overflow:hidden ancestors don't clip the tooltip
  popperOptions: {
    modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
  },
}

function sanitizeContent(content) {
  return typeof content === 'string' ? sanitizeHtml(content) : content
}

function resolveProps(value, modifiers) {
  if (value == null || value === false) return null

  let content, popperClass, delay, arrow, offset
  if (typeof value === 'string') {
    content = value
  } else if (typeof value === 'object') {
    content = value.content
    popperClass = value.popperClass
    delay = value.delay
    arrow = value.arrow
    offset = value.offset
  }
  if (!content) return null

  let placement = 'top'
  for (const pos of ['top', 'bottom', 'left', 'right']) {
    if (modifiers[pos]) {
      placement = pos
      break
    }
  }
  if (typeof value === 'object' && value.placement) placement = value.placement

  return { content, placement, popperClass, delay, arrow, offset }
}

function buildTippyProps(opts) {
  return {
    content: sanitizeContent(opts.content),
    placement: opts.placement,
    ...(opts.popperClass && { theme: opts.popperClass }),
    ...(opts.delay !== undefined && { delay: opts.delay }),
    ...(opts.arrow !== undefined && { arrow: opts.arrow }),
    ...(opts.offset !== undefined && { offset: opts.offset }),
  }
}

export const tippyDirective = {
  beforeMount(el, { value, modifiers }) {
    const opts = resolveProps(value, modifiers)
    if (!opts) return

    el._tippy_instance = tippy(el, {
      ...defaults,
      ...buildTippyProps(opts),
    })
  },

  updated(el, { value, oldValue, modifiers }) {
    if (value === oldValue) return

    const opts = resolveProps(value, modifiers)

    if (!opts) {
      if (el._tippy_instance) {
        el._tippy_instance.destroy()
        el._tippy_instance = null
      }
      return
    }

    if (el._tippy_instance) {
      el._tippy_instance.setProps(buildTippyProps(opts))
    } else {
      el._tippy_instance = tippy(el, {
        ...defaults,
        ...buildTippyProps(opts),
      })
    }
  },

  beforeUnmount(el) {
    if (el._tippy_instance) {
      el._tippy_instance.destroy()
      el._tippy_instance = null
    }
  },
}
